import {Spine} from '@fantasmagames/core/src/spine/Spine';
import {View} from '@fantasmagames/slot/src/common/view/View';
import {GenericButton} from '@fantasmagames/slot/src/modules/popupmenus/GenericButton';
import {Position} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {
	InteractiveScrollbar,
	Scrollbar,
	ScrollbarConstants,
} from '@fantasmagames/slot/src/modules/popupmenus/Scrollbar';
import {DraggingComponent} from '@fantasmagames/core/src/interaction/DraggingComponent';
import {SpriteGraphics} from '@fantasmagames/core/src/display/SpriteGraphics';
import {GameUtils} from '@fantasmagames/slot/src/utils/GameUtils';
import {SpineButton} from '@fantasmagames/core/src/display/SpineButton';
import BoneData = PIXI.spine.core.BoneData;

export class ButtonsMenu extends View {
	public static readonly Y_BUTTON_OFFSET: number = 90;
	// TODO: Place these on device instead?

	protected _buttons: GenericButton[] = [];
	protected _buttonsContainer: PIXI.Container;
	protected _buttonsText: PIXI.Text;
	protected _genericButton: GenericButton;

	protected _buttonDisabledTint: number = 0x111100;

	// TODO: Add currently selected item. Which was the last valid selection.
	protected _lastAcceptedSelection: GenericButton;

	protected _spineLayout: Spine;

	protected _buttonValues: string[] | number[] = [];

	protected _cancelButton: SpineButton;
	protected _acceptButton: SpineButton;
	protected _acceptCancelContainer: PIXI.Container;

	protected _acceptEnabled: boolean = false;

	protected _background: PIXI.Container;
	protected _titleBackground: PIXI.Container;

	protected _buttonsCallback: Function;
	protected _tapDownCallback: Function;
	protected _mouseOutCallback: Function;

	protected _titleText: string;
	protected _menuTitle: PIXI.Text;

	protected _buttonStyle: PIXI.TextStyle;
	protected _titleStyle: PIXI.TextStyle;
	protected _buttonOverStyle: PIXI.TextStyle;
	protected _buttonDownStyle: PIXI.TextStyle;
	protected _buttonDisabledStyle: PIXI.TextStyle;

	protected _xOffset: number;
	protected _yOffset: number;

	protected _draggingComponent: DraggingComponent;
	protected _scrollbar: Scrollbar;

	public init(): void {
		super.init();

		this.setupTextStyles();
		this.setupBaseLayout();
		this.setupBaseButtons();
	}

	public show(): void {
		super.show();
		this.enableInput();
		this.emitOpened();
	}

	public hide(): void {
		super.hide();
		this.disableInput();
		this.emitClosed();
	}

	public preCallback(valueId: number, ...args: any[]): void {
		for (const button of this._buttons) {
			if (button.id !== valueId) {
				button.resetState();
			}
		}
	}

	public set title(title: string) {
		this._menuTitle.text = title;
	}

	public set callback(callback: Function) {
		this._buttonsCallback = callback;
	}

	public set tapDownCallback(callback: Function) {
		this._tapDownCallback = callback;
	}

	public set mouseOutCallback(callback: Function) {
		this._mouseOutCallback = callback;
	}

	public set offset([xOffset, yOffset]: [number, number]) {
		this._xOffset = xOffset;
		this._yOffset = yOffset;
	}

	public set buttonValues(buttonValues: string[] | number[]) {
		this._buttonValues = buttonValues;
		this.generateButtons();
	}

	public set buttonDisabledTint(value: number) {
		this._buttonDisabledTint = value;
	}

	protected enableInput(): void {
		if (this._draggingComponent) {
			this._draggingComponent.enable();
		}
	}

	protected disableInput(): void {
		if (this._draggingComponent) {
			this._draggingComponent.disable();
		}
	}

	protected emitOpened(): void {
	}

	protected emitClosed(): void {
	}

	protected emitButtonPressed(): void {
	}

	protected emitAccepted(): void {
	}

	protected emitCancelled(): void {
	}

	protected onCancelPressed(): void {
		this.soundPlayer.play('buttonClick');

		this.emitCancelled();
		// this.hide();
	}

	protected onAcceptPressed(): void {
		this.soundPlayer.play('buttonClick');

		this.emitAccepted();
		// this.hide();
	}

	protected setupTextStyles(): void {
		this._titleStyle = this.textStyles.buttonMenuTitleStyle;
		// this._titleStyle.wordWrapWidth = this.resolutionManager.resolution.width;

		this._buttonStyle = this.textStyles.buttonBaseStyle;
		this._buttonOverStyle = this.textStyles.buttonOverStyle;
		this._buttonDownStyle = this.textStyles.buttonDownStyle;
		this._buttonDisabledStyle = this.textStyles.buttonDisabledStyle;
	}

	protected setupBaseLayout(): void {
		this._spineLayout = new Spine(this.resources.getSpine('BOX_MENU.json'));

		this._background = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('BACKGROUND_SOLID')];
		this._titleBackground = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('MENU_TITLE_BACKGROUND')];

		const menuTitleBone = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('MENU_TITLE')];
		const containerBox = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('BOX_CONTAINER')];
		const acceptButton = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('ACCEPT_BUTTON')];
		const acceptConfirmButton = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('ACCEPT_BUTTON_CONFIRM')];
		const declineButton = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('DECLINE_BUTTON')];
		const declineConfirmButton = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('DECLINE_BUTTON_EXIT')];

		const cleanChildrenList = [containerBox, acceptButton, acceptConfirmButton, declineButton, declineConfirmButton];

		for (const child of cleanChildrenList) {
			child.removeChildren();
		}

		this._background.interactive = true;

		this._menuTitle = new PIXI.Text('Title', this._titleStyle);
		this._menuTitle.anchor.set(0.5, 0.5);
		menuTitleBone.addChild(this._menuTitle);
		this._menuTitle.transform = menuTitleBone.children[0].transform;

		this.container.addChild(this._spineLayout);
		this._spineLayout.position.set(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height);
		this._spineLayout.update(0);
	}

	protected setupBaseButtons(): void {
		this._acceptCancelContainer = new PIXI.Container();
		this.container.addChild(this._acceptCancelContainer);

		const acceptPosition: BoneData = this._spineLayout.skeleton.findBone('ACCEPT_BUTTON').data;
		const cancelPosition: BoneData = this._spineLayout.skeleton.findBone('DECLINE_BUTTON').data;

		this._acceptButton = this.spineUtils.createSpineButton('ACCEPT_BUTTON.json', this.onAcceptPressed.bind(this));
		this._cancelButton = this.spineUtils.createSpineButton('DECLINE_BUTTON.json', this.onCancelPressed.bind(this));

		this._acceptButton.position.set(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height);
		this._cancelButton.position.set(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height);

		this._acceptButton.setTransform(
			this.resolutionManager.halfResolution.width + acceptPosition.x,
			this.resolutionManager.resolution.height - (this.resolutionManager.halfResolution.height + acceptPosition.y),
		);

		this._cancelButton.setTransform(
			this.resolutionManager.halfResolution.width + cancelPosition.x,
			this.resolutionManager.resolution.height - (this.resolutionManager.halfResolution.height + cancelPosition.y),
		);

		this._acceptCancelContainer.addChild(this._acceptButton);
		this._acceptCancelContainer.addChild(this._cancelButton);

		this.offset = [this._acceptButton.container.width, this._acceptButton.container.height];
	}

	protected generateButtons(): void {
		if (this._buttonValues) {
			this._buttonsContainer = new PIXI.Container();
			this.container.addChildAt(this._buttonsContainer, this.container.children.length - 1);

			for (const buttonValue of this._buttonValues) {
				const button = this.createButton(buttonValue);
				this._buttons.push(button);
				this._buttonsContainer.addChild(button);
			}

			this.layoutButtons();
		}
	}

	protected layoutButtons(): void {
		const boxMenuSkeleton = this.resources.getSpine('BOX_MENU.json');

		const boxContainer = this.spineUtils.getBoneAbsolutePosition(boxMenuSkeleton, 'BOX_CONTAINER');
		const yPosTop = this.spineUtils.getBoneAbsolutePosition(boxMenuSkeleton, 'MENU_TITLE_BONE').y;
		const slotAttachmentDataTop = this.spineUtils.getSlotAttachmentDimensions(boxMenuSkeleton, 'MENU_TITLE_BACKGROUND');

		const startY =
			this.resolutionManager.halfResolution.height - yPosTop + slotAttachmentDataTop.height * slotAttachmentDataTop.scale.y * 0.5;

		const acceptBone = this._spineLayout.skeleton.findBone('ACCEPT_BUTTON').data;
		const declineBone = this._spineLayout.skeleton.findBone('DECLINE_BUTTON').data;

		const buttonItem = this._buttons[0].children[0] as PIXI.Sprite;
		const halfButtonDimension = {x: buttonItem.width * 0.5, y: buttonItem.height * 0.5};

		const layoutLimit = {
			x: 0,
			y: this.resolutionManager.halfResolution.height - acceptBone.y - startY - halfButtonDimension.y,
		};

		const initialPos = this.spineUtils.getBoneAbsolutePosition(this.resources.getSpine('BOX_MENU.json'), 'BOX_CONTAINER');

		const startPosition = {
			x: this.resolutionManager.halfResolution.width - halfButtonDimension.x * 3,
			y: this.resolutionManager.halfResolution.height - boxContainer.y,
		};

		let xPosition = 0;
		let yPosition = 0;

		for (let button = 0; button < this._buttons.length; button++) {
			xPosition = this._xOffset * (button % 4) + startPosition.x;
			yPosition = this._yOffset * Math.floor(button / 4) + startPosition.y;

			this._buttons[button].position.set(xPosition, yPosition);
		}

		yPosition -= startY - this._yOffset;

		if (yPosition >= layoutLimit.y) {
			const scrollBarWidth: number = this.device.isMobile
				? ScrollbarConstants.SCROLLBAR_WIDTH_MOBILE
				: ScrollbarConstants.SCROLLBAR_WIDTH;
			const scrollbarPosition: Position = {
				x: this.resolutionManager.resolution.width - scrollBarWidth,
				// x: startPosition.x - halfButtonDimension.x - scrollBarWidth * 2,
				// y: startPosition.y - halfButtonDimension.y,
				y: startY,
			};
			const scrollbarHeight: number = layoutLimit.y;
			const containerVisibleHeight = layoutLimit.y;
			const containerTotalHeight = yPosition;

			this.setupButtonsMask(this.resolutionManager.resolution.width, layoutLimit.y, scrollbarPosition.y);

			this._scrollbar = this.setupScrollbar(
				scrollBarWidth,
				scrollbarHeight,
				containerVisibleHeight,
				containerTotalHeight,
				scrollbarPosition,
			);

			this._draggingComponent = new DraggingComponent(
				this.updateManager,
				this._buttonsContainer,
				this._background.children[0] as PIXI.Sprite,
				this.device.isMobile,
			);

			const limits = {
				maxX: this._buttonsContainer.x,
				maxY: 0,
				minX: this._buttonsContainer.x,
				minY: -(yPosition - layoutLimit.y),
			};

			(this._scrollbar as InteractiveScrollbar).setupInteraction(
				this.updateManager,
				this._buttonsContainer,
				this.device.isMobile,
				limits,
			);

			this._draggingComponent.canDrag = true;
			this._draggingComponent.addlisteningComponent(this._scrollbar);

			this._draggingComponent.maxY = 0;
			this._draggingComponent.minY = -(yPosition - layoutLimit.y);
		}
	}

	protected setupButtonsMask(maskWidth: number, maskHeight: number, positionY: number = 0, positionX: number = 0): void {
		const buttonsMask = SpriteGraphics.makeRectangle(maskWidth, maskHeight);

		this.container.addChild(buttonsMask);
		buttonsMask.position.set(positionX, positionY);
		this._buttonsContainer.mask = buttonsMask;
	}

	protected setupScrollbar(
		barWidth: number,
		barheight: number,
		containerVisibleHeight: number,
		containerTotalHeight: number,
		barposition: Position,
	): Scrollbar {
		const scrollBar = new InteractiveScrollbar(barWidth, barheight, containerVisibleHeight, containerTotalHeight);

		this.container.addChild(scrollBar);
		scrollBar.position.set(barposition.x, barposition.y);

		return scrollBar;
	}

	protected createButton(value: any): GenericButton {
		// console.warn(value);
		const button = new GenericButton(this._buttonsCallback.bind(this, value));
		if (this._tapDownCallback) {
			button.tapDownCallback = this._tapDownCallback.bind(this, value);
		}
		if (this._mouseOutCallback) {
			button.mouseOutCallback = this._mouseOutCallback.bind(this, value);
		}
		button.id = value;

		const textValue = this.formatText(value);

		const up = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'MENU_BUTTONS.png'));
		const down = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'MENU_BUTTONS_WHITE.png'));
		const over = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'MENU_BUTTONS_HOVER.png'));
		const disabled = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'MENU_BUTTONS.png'));

		up.anchor.set(0.5, 0.5);
		down.anchor.set(0.5, 0.5);
		over.anchor.set(0.5, 0.5);
		disabled.anchor.set(0.5, 0.5);

		const textUp = new PIXI.Text(textValue, this._buttonStyle);
		textUp.anchor.set(0.5, 0.5);
		up.addChild(textUp);

		const textDown = new PIXI.Text(textValue, this._buttonDownStyle);
		textDown.anchor.set(0.5, 0.5);
		down.addChild(textDown);

		const textOver = new PIXI.Text(textValue, this._buttonOverStyle);
		textOver.anchor.set(0.5, 0.5);
		over.addChild(textOver);

		const textDisabled = new PIXI.Text(textValue, this._buttonDisabledStyle);
		textDisabled.anchor.set(0.5, 0.5);
		disabled.addChild(textDisabled);

		const maxSize = {x: up.width * 0.85, y: up.height * 0.9}; // 0.9 is for button padding in this case. So the text doesn't go out to the edges of the sprite size.
		GameUtils.correctSpriteScale(textUp, maxSize);
		GameUtils.correctSpriteScale(textDown, maxSize);
		GameUtils.correctSpriteScale(textOver, maxSize);
		GameUtils.correctSpriteScale(textDisabled, maxSize);

		disabled.tint = this.buttonDisabledTint;

		button.setFrames(up, down, over, disabled);
		// button.anchor.set(0.5, 0.5);

		return button;
	}

	protected formatText(text: any): string {
		return text;
	}

	protected setupButtonEvents(): void {
	}

	protected clearButtons(): void {
		if (this._buttons.length === 0) {
			return;
		}

		for (let index = this._buttons.length; index > 0; index--) {
			const element = this._buttons[index];
			element.destroy();
		}

		this._buttonValues = [];
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/popupmenus/ButtonsMenu.ts